/*
 * Shoreline Shoes — Boost "Select Options" quick view: mini-PDP behaviour
 *
 * Works alongside shoreline-boost-quickview.css. On every open / re-render
 * of #boost-sd__modal-quickview it:
 *   1) trims size labels to the PDP format   "5 Medium" → "5M", "7 Wide" → "7W"
 *   2) shows the chosen value next to each option title ("Size/Width: 5 Medium")
 *   3) adds the "Save N%" pill after the price when a compare-at price exists
 *   4) fetches the PDP condition selector for the product
 *      (/products/<handle>?section_id=shoreline-qv-condition) and inserts it
 *      under the price — same New / Excellent / Gently Worn tiles as the PDP
 *   4c) from the same response, lifts the Loox star rating ([data-sh-qv-rating],
 *      Liquid-rendered from the Loox metafields) and places it under the title —
 *      only when the product has reviews, so no empty stars
 *   5) nudges Boost's image slider (rendered hidden) so it lays out correctly
 */
(function () {
  'use strict';

  var MODAL_ID = 'boost-sd__modal-quickview';
  var SECTION_ID = 'shoreline-qv-condition';
  var conditionCache = {};
  var ratingCache = {};
  var observer = null;
  var scheduled = false;

  /* ---------- helpers ---------- */

  function abbrevSize(value) {
    var m = String(value || '').trim().match(/^(\d+(?:\.\d+)?(?:\/\d+)?)\s*(.*)$/);
    if (!m) return null;
    var rest = m[2].trim();
    if (!rest) return m[1];
    var letters = rest
      .split(/[\s-]+/)
      .filter(Boolean)
      .map(function (w) { return w.charAt(0).toUpperCase(); })
      .join('');
    return m[1] + letters;
  }

  function parseMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  function getHandle(modal) {
    var raw = modal.getAttribute('data-product');
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      return data && data.handle ? data.handle : null;
    } catch (e) {
      var m = raw.match(/"handle"\s*:\s*"([^"]+)"/);
      return m ? m[1] : null;
    }
  }

  function isVisible(el) {
    return !!el && el.style.display !== 'none' && el.getClientRects().length > 0;
  }

  /* ---------- 1) size labels ---------- */

  function trimSizes(modal) {
    modal
      .querySelectorAll('.boost-sd__product-swatch-option[data-swatch-by="text"] .boost-sd__radio-label')
      .forEach(function (label) {
        if (label.dataset.shQvFull) return;
        var full = label.textContent.trim();
        if (!full || !/^\d/.test(full)) return;
        var short = abbrevSize(full);
        if (!short || short === full) {
          label.dataset.shQvFull = full;
          return;
        }
        label.dataset.shQvFull = full;
        label.setAttribute('title', full);
        label.textContent = short;
      });
  }

  /* ---------- 2) chosen value in option titles ---------- */

  function chosenValue(swatch) {
    var selected = swatch.querySelector('.boost-sd__radio-label--selected');
    if (!selected) return '';
    var input = selected.parentElement ? selected.parentElement.querySelector('input.boost-sd__radio-input') : null;
    return (selected.dataset.shQvFull || (input && input.value) || selected.textContent || '').trim();
  }

  function decorateTitles(modal) {
    modal.querySelectorAll('.boost-sd__product-swatch').forEach(function (swatch) {
      var title = swatch.querySelector('.boost-sd__product-swatch-title');
      if (!title) return;
      var chosen = title.querySelector('.sh-qv-chosen');
      if (!chosen) {
        var name = title.textContent.replace(/\s*:\s*$/, '').trim();
        title.textContent = '';
        var nameEl = document.createElement('span');
        nameEl.className = 'sh-qv-optname';
        nameEl.textContent = name + ':';
        chosen = document.createElement('span');
        chosen.className = 'sh-qv-chosen';
        title.appendChild(nameEl);
        title.appendChild(chosen);
      }
      var value = chosenValue(swatch);
      if (chosen.textContent !== value) chosen.textContent = value;
    });
  }

  /* ---------- 3) Save % pill ---------- */

  function savePill(modal) {
    var priceWrap = modal.querySelector('.boost-sd__quick-view-price');
    if (!priceWrap) return;
    var compareEl = priceWrap.querySelector('.boost-sd__format-currency--price-compare');
    var currentEl = null;
    priceWrap.querySelectorAll('.boost-sd__format-currency').forEach(function (el) {
      if (!currentEl && !el.classList.contains('boost-sd__format-currency--price-compare')) currentEl = el;
    });
    var pill = priceWrap.querySelector('.sh-qv-save');
    var current = currentEl ? parseMoney(currentEl.textContent) : null;
    var compare = compareEl ? parseMoney(compareEl.textContent) : null;

    if (current === null || compare === null || compare <= current) {
      if (pill) pill.remove();
      return;
    }
    var pct = Math.round(((compare - current) / compare) * 100);
    if (pct <= 0) {
      if (pill) pill.remove();
      return;
    }
    var text = 'Save ' + pct + '%';
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'sh-qv-save';
      priceWrap.appendChild(pill);
    }
    if (pill.textContent !== text) pill.textContent = text;
    if (pill !== priceWrap.lastElementChild) priceWrap.appendChild(pill);
  }

  /* ---------- 4) condition selector ---------- */

  function insertCondition(modal, handle, html) {
    var info = modal.querySelector('.boost-sd__quick-view-product-info');
    if (!info) return;
    var existing = info.querySelector('.sh-qv-condition-wrap');
    if (existing && existing.dataset.handle === handle) return;
    if (existing) existing.remove();
    if (!html) return;

    var wrap = document.createElement('div');
    wrap.className = 'sh-qv-condition-wrap';
    wrap.dataset.handle = handle;
    wrap.innerHTML = html;

    var price = info.querySelector('.boost-sd__quick-view-price');
    if (price && price.parentNode === info) {
      info.insertBefore(wrap, price.nextSibling);
    } else {
      var title = info.querySelector('.boost-sd__quick-view-title');
      if (title && title.parentNode === info) info.insertBefore(wrap, title.nextSibling);
      else info.insertBefore(wrap, info.firstChild);
    }
  }

  /* ---------- 4c) Loox rating under the title ---------- */

  function insertRating(modal, handle, html) {
    var info = modal.querySelector('.boost-sd__quick-view-product-info');
    if (!info) return;
    var existing = info.querySelector('.sh-qv-rating-wrap');
    if (existing && existing.dataset.handle === handle) return;
    if (existing) existing.remove();
    if (!html) return;

    var wrap = document.createElement('div');
    wrap.className = 'sh-qv-rating-wrap';
    wrap.dataset.handle = handle;
    wrap.innerHTML = html;

    var title = info.querySelector('.boost-sd__quick-view-title');
    if (title && title.parentNode === info) {
      info.insertBefore(wrap, title.nextSibling);
    } else {
      var price = info.querySelector('.boost-sd__quick-view-price');
      if (price && price.parentNode === info) info.insertBefore(wrap, price);
      else info.insertBefore(wrap, info.firstChild);
    }
  }

  function loadCondition(modal) {
    var handle = getHandle(modal);
    if (!handle) return;
    if (modal.dataset.shQvHandle === handle && modal.querySelector('.sh-qv-condition-wrap[data-handle="' + handle + '"]')) return;
    modal.dataset.shQvHandle = handle;

    var stale = modal.querySelector('.sh-qv-condition-wrap');
    if (stale && stale.dataset.handle !== handle) stale.remove();

    var staleRating = modal.querySelector('.sh-qv-rating-wrap');
    if (staleRating && staleRating.dataset.handle !== handle) staleRating.remove();

    if (Object.prototype.hasOwnProperty.call(conditionCache, handle)) {
      insertCondition(modal, handle, conditionCache[handle]);
      insertRating(modal, handle, ratingCache[handle] || '');
      return;
    }

    var url = '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (text) {
        var html = '';
        var ratingHtml = '';
        if (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var block = doc.querySelector('[data-sh-qv-condition]');
          if (block) {
            // Carry the snippet's stylesheet link along (it sits just before the <nav>)
            var link = doc.querySelector('link[href*="product-condition-selector"]');
            html = (link ? link.outerHTML : '') + block.outerHTML;
          }
          var rating = doc.querySelector('[data-sh-qv-rating]');
          if (rating) ratingHtml = rating.outerHTML;
        }
        conditionCache[handle] = html;
        ratingCache[handle] = ratingHtml;
        if (getHandle(modal) === handle) {
          insertCondition(modal, handle, html);
          insertRating(modal, handle, ratingHtml);
        }
      })
      .catch(function () {
        conditionCache[handle] = '';
        ratingCache[handle] = '';
      });
  }

  /* ---------- 4a) condition sentence: one line + More/Less when it overflows ---------- */

  function clampCondition(modal) {
    modal.querySelectorAll('.sh-qv-condition__text[data-sh-qv-clamp]').forEach(function (p) {
      if (p.dataset.shQvClampDone === '1') return;
      if (!p.getClientRects().length) return; // not laid out yet — try again on the next pass
      p.dataset.shQvClampDone = '1';

      // Measure unclamped: does the full text need more than one line?
      var lineHeight = parseFloat(getComputedStyle(p).lineHeight) || 20;
      p.classList.remove('is-clamped');
      var fullHeight = p.getBoundingClientRect().height;
      p.classList.add('is-clamped');
      var overflows = fullHeight > lineHeight * 1.5;
      if (!overflows) {
        p.classList.remove('is-clamped');
        return;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sh-qv-condition__toggle';
      btn.textContent = 'More';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var expanded = p.classList.toggle('is-expanded');
        p.classList.toggle('is-clamped', !expanded);
        btn.textContent = expanded ? 'Less' : 'More';
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
      p.parentNode.insertBefore(btn, p.nextSibling);
    });
  }

  /* ---------- 4b) button labels — match the PDP wording ---------- */

  var BUTTON_LABELS = { 'add to cart': 'Add to Cart', 'buy it now': 'Buy Now' };

  function relabelButtons(modal) {
    modal.querySelectorAll('.boost-sd__quick-view-btn .boost-sd__button-text').forEach(function (el) {
      var key = el.textContent.trim().toLowerCase();
      if (BUTTON_LABELS[key] && el.textContent !== BUTTON_LABELS[key]) el.textContent = BUTTON_LABELS[key];
    });
  }

  /* ---------- 4d) availability — cross out combos that don't exist / are sold out ---------- */

  function optionKey(name) {
    return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function labelValue(label) {
    var input = label.parentElement ? label.parentElement.querySelector('input.boost-sd__radio-input') : null;
    return (label.dataset.shQvFull || (input && input.value) || label.textContent || '').trim();
  }

  function getVariants(modal) {
    var handle = getHandle(modal);
    if (modal.__shQvVariants && modal.__shQvVariantsHandle === handle) return modal.__shQvVariants;
    var raw = modal.getAttribute('data-product');
    var variants = [];
    try {
      var data = JSON.parse(raw);
      (data && data.variants ? data.variants : []).forEach(function (v) {
        var opts = v.original_merged_options || v.merged_options || [];
        var map = {};
        var keys = [];
        opts.forEach(function (o) {
          var i = String(o).indexOf(':');
          if (i < 0) return;
          var k = o.slice(0, i);
          keys.push(k);
          map[k] = o.slice(i + 1).trim().toLowerCase();
        });
        variants.push({ map: map, keys: keys, available: !!v.available });
      });
    } catch (e) {
      variants = [];
    }
    modal.__shQvVariants = variants;
    modal.__shQvVariantsHandle = handle;
    return variants;
  }

  function updateAvailability(modal) {
    var variants = getVariants(modal);
    if (!variants.length) return;
    var keyOrder = variants[0].keys;

    var groups = [];
    modal.querySelectorAll('.boost-sd__product-swatch').forEach(function (g, index) {
      var nameEl = g.querySelector('.sh-qv-optname') || g.querySelector('.boost-sd__product-swatch-title');
      var name = nameEl ? nameEl.textContent.replace(/:\s*$/, '').trim() : '';
      var key = optionKey(name);
      if (!(key in variants[0].map) && keyOrder[index]) key = keyOrder[index]; // fall back to position
      var selected = g.querySelector('.boost-sd__radio-label--selected');
      groups.push({
        key: key,
        labels: Array.prototype.slice.call(g.querySelectorAll('.boost-sd__radio-label')),
        selected: selected ? labelValue(selected).toLowerCase() : ''
      });
    });

    groups.forEach(function (group) {
      group.labels.forEach(function (label) {
        var value = labelValue(label).toLowerCase();
        var ok = variants.some(function (v) {
          if (!v.available) return false;
          if ((v.map[group.key] || '') !== value) return false;
          return groups.every(function (other) {
            if (other === group || !other.selected) return true;
            return (v.map[other.key] || '') === other.selected;
          });
        });
        if (label.classList.contains('sh-qv-unavailable') === ok) label.classList.toggle('sh-qv-unavailable', !ok);
      });
    });
  }

  /* ---------- 4c) "View full details" — inside the sticky button bar on mobile ---------- */

  var mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;

  function placeDetailsLink(modal) {
    var link = modal.querySelector('.boost-sd__quick-view-details-link');
    var bar = modal.querySelector('.boost-sd__quick-view-btn');
    var info = modal.querySelector('.boost-sd__quick-view-product-info');
    if (!link || !bar || !info) return;
    var mobile = mobileQuery ? mobileQuery.matches : window.innerWidth < 768;
    if (mobile) {
      if (link.parentNode !== bar) bar.appendChild(link);
    } else if (link.parentNode === bar) {
      info.insertBefore(link, bar.nextSibling); // back to Boost's original spot under the buttons
    }
  }

  /* ---------- 5) slider nudge ---------- */

  function nudgeSlider(modal) {
    if (!isVisible(modal)) {
      modal.dataset.shQvNudged = '';
      return;
    }
    if (modal.dataset.shQvNudged === '1') return;
    modal.dataset.shQvNudged = '1';
    setTimeout(function () {
      window.dispatchEvent(new Event('resize'));
    }, 60);
  }

  /* ---------- orchestration ---------- */

  function process(modal) {
    if (!modal.querySelector('.boost-sd__quick-view')) return;
    trimSizes(modal);
    decorateTitles(modal);
    savePill(modal);
    loadCondition(modal);
    clampCondition(modal);
    relabelButtons(modal);
    placeDetailsLink(modal);
    updateAvailability(modal);
    nudgeSlider(modal);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout rather than requestAnimationFrame: rAF pauses in background tabs
    setTimeout(function () {
      scheduled = false;
      var modal = document.getElementById(MODAL_ID);
      if (!modal) return;
      try {
        process(modal);
      } finally {
        if (observer) observer.takeRecords(); // drop mutations caused by our own edits
      }
    }, 0);
  }

  /*
   * Boost destroys and re-creates #boost-sd__modal-quickview on EVERY open, so
   * an observer bound to one modal element goes stale after the first open.
   * Watch the document instead and only react to mutations inside the modal.
   */
  function touchesModal(records) {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return false;
    for (var i = 0; i < records.length; i++) {
      var t = records[i].target;
      if (t === modal || modal.contains(t)) return true;
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j] === modal || (added[j].contains && added[j].contains(modal))) return true;
      }
    }
    return false;
  }

  function boot() {
    observer = new MutationObserver(function (records) {
      if (touchesModal(records)) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-product']
    });

    // Belt and braces: after a "Select Options" click, re-check a few times while Boost renders
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (!e.target.closest('.boost-sd__button--select-option, .boost-sd__button--quick-view')) return;
      [150, 500, 1200, 2500].forEach(function (ms) { setTimeout(schedule, ms); });
    }, true);

    // Re-place the details link when crossing the mobile breakpoint
    window.addEventListener('resize', schedule);

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
